/**
 * Phones (< 768px, everyone; UI review High #5): the bottom tab bar uses the same areas as the desktop sidebar
 * (backlog #53): Home · Chat · Shifts · Tasks · Fleet, each only if you can open something in it, then More.
 * Tapping an area opens the last page you used there; More shows that area's full list plus Settings, Help and Assistant.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { Menu as MenuIcon, Smartphone } from 'lucide-react';
import { CountPill } from '@/components/ui';
import { useNavArea, useNavBadges } from '@/lib/navArea';
import { Sidebar } from './Sidebar';
import { useIsPhone, useLayoutPref } from '@/mobile/layout';

/** Short tab labels where the full area name doesn't fit a phone tab. */
const SHORT: Record<string, string> = { shifts: 'Shifts' };

export function MobileTabBar({ onMore, moreOpen }: { onMore: () => void; moreOpen: boolean }) {
  const { areas, selected, open } = useNavArea();
  const badges = useNavBadges();
  const cls = (active: boolean) => clsx('relative flex min-h-[52px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 text-[11px]', active ? 'text-chrome-active' : 'text-white/75');
  return (
    <nav className="on-dark flex shrink-0 bg-chrome-bar pb-[env(safe-area-inset-bottom)]" aria-label="Main" data-testid="mobile-tabbar">
      {areas.map(a => {
        const n = badges.area(a.id);
        const active = !moreOpen && a.id === selected;
        return (
          <button key={a.id} type="button" onClick={() => open(a.id)} className={cls(active)} aria-current={active ? 'page' : undefined} data-testid={`tab-${a.id}`}
            aria-label={n ? `${a.label}, ${n} waiting` : a.label}>
            <a.icon className="h-5 w-5" /><span className="truncate">{SHORT[a.id] ?? a.label}</span>
            {n > 0 && <span className="absolute right-[18%] top-1"><CountPill n={n} /></span>}
          </button>
        );
      })}
      <button type="button" onClick={onMore} aria-expanded={moreOpen} aria-controls="mobile-more" className={cls(moreOpen)} data-testid="tab-more"><MenuIcon className="h-5 w-5" />More</button>
    </nav>
  );
}

/** Full menu as a left sheet; closes on navigation, Escape or tapping the backdrop. */
export function MoreSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const loc = useLocation();
  // "Desktop site" was chosen in Me on a phone: offer the way back.
  const phone = useIsPhone();
  const setPref = useLayoutPref(s => s.setPref);
  useEffect(() => { onClose(); }, [loc.pathname, loc.search]); // eslint-disable-line react-hooks/exhaustive-deps -- close on route change only
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [open, onClose]);
  return (
    <div id="mobile-more" className={clsx('fixed inset-0 z-50', !open && 'pointer-events-none')} aria-hidden={!open}>
      <div className={clsx('absolute inset-0 bg-black/50 transition-opacity', open ? 'opacity-100' : 'opacity-0')} onClick={onClose} />
      <div className={clsx('absolute inset-y-0 left-0 max-w-[85vw] shadow-2xl transition-transform duration-150', open ? 'translate-x-0' : 'invisible -translate-x-full')} role="dialog" aria-modal="true" aria-label="Menu">
        <Sidebar pinned={false} />
        {phone && (
          <button type="button" onClick={() => setPref('auto')} data-testid="mobile-site"
            className="absolute bottom-[calc(env(safe-area-inset-bottom)+12px)] right-3 flex min-h-[44px] items-center gap-2 rounded-full bg-accent px-4 text-sm font-medium text-on-accent shadow-lg ring-1 ring-black">
            <Smartphone className="h-4 w-4" />Mobile site
          </button>
        )}
      </div>
    </div>
  );
}
